import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { HeartIcon } from '@heroicons/react/24/outline';

import { getHealthPlanImportById } from '../services/healthPlanImportsService';
import type { HealthPlanCopayRecord, HealthPlanImportDocument, HealthPlanLifeRecord } from '../services/healthPlanTypes';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';
import { useToast } from '@/hooks/useToast';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatCpf = (cpf?: string | null): string => {
  if (!cpf) return '—';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatCertificateCode = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes('/')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length <= 2) return trimmed;
  const prefix = digits.slice(0, -2);
  const suffix = digits.slice(-2);
  return `${prefix}/${suffix}`;
};

const formatCpfOrCode = (life?: HealthPlanLifeRecord | null): string => {
  if (!life) return '—';
  const normalizedCpf = (life.beneficiaryCpf || '').replace(/\D/g, '');
  if (normalizedCpf.length === 11) {
    return formatCpf(life.beneficiaryCpf);
  }
  const certificateLike =
    formatCertificateCode(life.certificateCode) || formatCertificateCode(life.userCode) || null;
  if (certificateLike) return certificateLike;
  const code =
    life.userCode ||
    life.certificateCode ||
    life.familyIdentifier ||
    (life.beneficiaryCpf ? life.beneficiaryCpf.trim() : '') ||
    life.holderCpf;
  return code && code.trim() ? code.trim() : '—';
};

export function HealthPlanImportCollaboratorDetailsPage() {
  const { importId, collaboratorId } = useParams<{ importId: string; collaboratorId: string }>();
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const { data: collaborators = [] } = useCollaboratorsRealtimeQuery();

  const [importData, setImportData] = useState<HealthPlanImportDocument | null>(null);
  const [holder, setHolder] = useState<HealthPlanLifeRecord | null>(null);
  const [dependents, setDependents] = useState<HealthPlanLifeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [copayEntries, setCopayEntries] = useState<HealthPlanCopayRecord[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!importId || !collaboratorId) return;
      try {
        setLoading(true);
        const data = await getHealthPlanImportById(importId);
        if (!data) {
          showError('Importação não encontrada.');
          navigate('/expenses/benefits/health-plan', { replace: true });
          return;
        }

        const families = (data as any).familiesPreview;
        let matchedHolder: any = null;
        let deps: any[] = [];

        if (Array.isArray(families) && families.length) {
          const target = families.find(
            (family: any) => family?.collaboratorId === collaboratorId || family?.holder?.collaboratorId === collaboratorId,
          );
          if (target) {
            matchedHolder = target.holder ?? null;
            deps = target.dependents ?? [];
          }
        }

        if (!matchedHolder) {
          showError('Titular não encontrado nesta importação.');
          navigate('/expenses/benefits/health-plan', { replace: true });
          return;
        }

        setImportData(data);
        setHolder(matchedHolder);
        setDependents(deps);
        const targetCpfs = new Set<string>();
        const addTarget = (value?: string | null) => {
          const normalized = (value || '').replace(/\D/g, '');
          if (normalized) targetCpfs.add(normalized);
        };
        addTarget(matchedHolder.familyIdentifier);
        addTarget(matchedHolder.beneficiaryCpf);
        addTarget(matchedHolder.holderCpf);
        deps.forEach((dep) => {
          addTarget(dep.beneficiaryCpf);
          addTarget(dep.familyIdentifier);
        });
        const copays = (data.copayPreview ?? []).filter((entry) => {
          const cpf = entry.beneficiaryCpf?.replace(/\D/g, '') ?? '';
          const holderCpf = entry.holderCpf?.replace(/\D/g, '') ?? '';
          return (cpf && targetCpfs.has(cpf)) || (holderCpf && targetCpfs.has(holderCpf));
        });
        setCopayEntries(copays);
      } catch (err) {
        console.error('[HealthPlanImportCollaboratorDetailsPage] Erro ao carregar registro', err);
        showError('Não foi possível carregar os detalhes do colaborador.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboratorId, importId]);

  const premiumTotal = useMemo(() => {
    const holderPremium = holder?.premiumTotal ?? 0;
    const dependentsPremium = dependents.reduce((acc, dep) => acc + (dep.premiumTotal ?? 0), 0);
    return holderPremium + dependentsPremium;
  }, [holder, dependents]);
  const holderCollaborator = useMemo(() => {
    if (!holder?.collaboratorId) return null;
    return (collaborators as any[]).find((collab) => collab?.id === holder.collaboratorId) ?? null;
  }, [collaborators, holder?.collaboratorId]);
  const holderDisplayName = holderCollaborator?.name ?? holder?.beneficiaryName ?? 'Colaborador';
  const totalCopay = useMemo(() => {
    const holderCopay = holder?.copayAmount ?? 0;
    const dependentsCopay = dependents.reduce((acc, dep) => acc + (dep.copayAmount ?? 0), 0);
    return holderCopay + dependentsCopay;
  }, [holder, dependents]);
  const totalCost = useMemo(() => {
    const holderCost = holder ? holder.totalCost ?? (holder.premiumTotal ?? 0) + (holder.copayAmount ?? 0) : 0;
    const dependentsCost = dependents.reduce(
      (acc, dep) => acc + (dep.totalCost ?? (dep.premiumTotal ?? 0) + (dep.copayAmount ?? 0)),
      0,
    );
    return holderCost + dependentsCost;
  }, [holder, dependents]);
  const competencePath =
    importData?.billingPeriod != null
      ? `/expenses/benefits/health-plan/imports/${importData.billingPeriod}`
      : '/expenses/benefits/health-plan';

  const renderLifeEntry = (life: HealthPlanLifeRecord, isHolder = false) => (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between text-sm font-semibold text-gray-900">
        <span>{isHolder ? 'Titular' : 'Dependente'}</span>
        <span className="text-xs text-gray-500">{formatCpfOrCode(life)}</span>
      </div>
      <div className="mt-1 text-sm text-gray-900">{life.beneficiaryName}</div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {(life.plans && life.plans.length
          ? life.plans
          : [
              {
                planName: life.planName,
                premiumTotal: life.premiumTotal,
                copayAmount: null,
              },
            ]
        ).map((plan, idx) => (
          <div
            key={`${plan.planName || 'plano'}-${idx}`}
            className="rounded border border-gray-200 bg-white p-2 text-sm"
          >
            <div className="font-semibold text-gray-900">{plan.planName || 'Plano'}</div>
            <div className="mt-1">
              <div className="text-gray-500">Prêmio</div>
              <div className="font-semibold text-gray-900">{formatCurrency(plan.premiumTotal ?? null)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <div className="text-sm text-gray-600">Carregando colaborador...</div>;
  }

  if (!importData || !holder) {
    return <div className="text-sm text-gray-600">Registro não encontrado.</div>;
  }

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo={competencePath}
        icon={<HeartIcon className="h-5 w-5" aria-hidden />}
        badge="Colaborador"
        title={holderDisplayName}
        description={`Competência: ${importData.billingPeriod ?? '—'}`}
        actions={
          <div className="flex flex-col items-start text-xs text-gray-500 sm:items-end">
            <span>Import ID: {importId}</span>
            <span>Colaborador ID: {collaboratorId}</span>
          </div>
        }
      />

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 text-sm">
            {[
              ['CPF / Código', formatCpfOrCode(holder)],
              ['Plano', holder.planName || '—'],
              ['Prêmio (titular + dependentes)', formatCurrency(premiumTotal)],
              ['Total (prêmio + copay)', formatCurrency(totalCost)],
            ].map(([label, value]) => (
              <div key={label as string} className="flex items-center justify-between">
                <span className="text-gray-600">{label}</span>
                <span className="font-semibold text-gray-900">{value as any}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm">
            {[
              ['Dependentes', dependents.length],
              ['Coparticipação (titular + dependentes)', formatCurrency(totalCopay)],
              ['Prêmio (somente titular)', formatCurrency(holder.premiumTotal)],
            ].map(([label, value]) => (
              <div key={label as string} className="flex items-center justify-between">
                <span className="text-gray-600">{label}</span>
                <span className="font-semibold text-gray-900">{value as any}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">Extrato de vidas</h2>
          {holder ? renderLifeEntry({ ...holder, beneficiaryName: holderDisplayName }, true) : null}
          {dependents.length ? (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase text-gray-500">Dependentes ({dependents.length})</div>
              <div className="space-y-2">
                {dependents.map((dep, idx) => (
                  <div key={`${dep.beneficiaryCpf}-${idx}`}>{renderLifeEntry(dep)}</div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-600">Nenhum dependente informado.</div>
          )}
          {copayEntries.length ? (
            <div className="space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                {copayEntries.map((entry, idx) => (
                  <div key={`${entry.beneficiaryCpf}-${entry.raw}-${idx}`} className="rounded-md border border-gray-200 bg-white p-3">
                    <div className="flex items-center justify-between text-sm text-gray-900">
                      <span className="font-semibold">{entry.procedure ?? 'Procedimento'}</span>
                      <span className="text-xs text-gray-500">{entry.serviceDate ?? ''}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-700">{entry.beneficiaryName}</div>
                    <div className="mt-1 text-xs text-gray-500">{formatCpf(entry.beneficiaryCpf)}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="text-sm text-gray-600">
        <Link to={competencePath} className="text-primary-700 underline underline-offset-2 hover:text-primary-800">
          Voltar para a importação
        </Link>
      </div>
    </div>
  );
}
